/**
 * Validates if an address line contains APO, FPO, or DPO (military postal) formats.
 * Used to prevent shipping to military postal addresses when needed.
 *
 * APO = Army Post Office
 * FPO = Fleet Post Office
 * DPO = Diplomatic Post Office
 *
 * These addresses typically use state codes: AE, AP, or AA
 */
// Military postal abbreviations
const MILITARY_POSTAL_CODES = ['apo', 'fpo', 'dpo'];
const MILITARY_STATE_CODES = ['ae', 'ap', 'aa'];
// Common street suffixes that indicate a street name rather than military postal
const STREET_SUFFIXES = [
    'street',
    'st',
    'avenue',
    'ave',
    'road',
    'rd',
    'drive',
    'dr',
    'lane',
    'ln',
    'boulevard',
    'blvd',
    'way',
    'circle',
    'cir',
    'court',
    'ct',
    'place',
    'pl',
];
// Words that contain military postal codes but aren't military addresses
const EXCLUDE_WORDS = ['apollo'];
// Regex patterns for exclusions (street addresses containing military codes)
const EXCLUDE_PATTERNS = [
    // Numbered street addresses with military codes
    ...MILITARY_POSTAL_CODES.map((code) => new RegExp(`\\b\\d+\\s+${code}\\s+(?:${STREET_SUFFIXES.join('|')})\\b`, 'i')),
    // Military codes followed by street suffixes (without numbers)
    ...MILITARY_POSTAL_CODES.map((code) => new RegExp(`\\b${code}\\s+(?:${STREET_SUFFIXES.join('|')})\\b`, 'i')),
    // Exclude words that contain military postal codes
    ...EXCLUDE_WORDS.map((word) => new RegExp(`\\b${word}\\b`, 'i')),
];
// Military postal validation patterns
const MILITARY_POSTAL_PATTERNS = [
    // Standard format: APO/FPO/DPO + state code + ZIP
    '(?:^|\\s)(?:apo|fpo|dpo)\\s+(?:ae|ap|aa)\\s+\\d{5}(?:-\\d{4})?(?:\\s|$)',
    // With state code but no ZIP
    '(?:^|\\s)(?:apo|fpo|dpo)\\s+(?:ae|ap|aa)(?:\\s|$)',
    // With ZIP but no state code
    '(?:^|\\s)(?:apo|fpo|dpo)\\s+\\d{5}(?:-\\d{4})?(?:\\s|$)',
    // With various separators
    '(?:^|\\s)(?:apo|fpo|dpo)[\\s-/\\\\]+(?:ae|ap|aa)[\\s-/\\\\]*\\d{5}(?:-\\d{4})?(?:\\s|$)',
    // Full form patterns
    '(?:^|\\s)army\\s+post\\s+office(?:\\s+(?:ae|ap|aa))?(?:\\s+\\d{5}(?:-\\d{4})?)?(?:\\s|$)',
    '(?:^|\\s)fleet\\s+post\\s+office(?:\\s+(?:ae|ap|aa))?(?:\\s+\\d{5}(?:-\\d{4})?)?(?:\\s|$)',
    '(?:^|\\s)diplomatic\\s+post\\s+office(?:\\s+(?:ae|ap|aa))?(?:\\s+\\d{5}(?:-\\d{4})?)?(?:\\s|$)',
    // Just abbreviations alone
    '(?:^|\\s)(?:apo|fpo|dpo)(?:\\s|$)',
    // With punctuation
    '(?:^|\\s)(?:a\\.p\\.o\\.|f\\.p\\.o\\.|d\\.p\\.o\\.)(?:\\s+(?:ae|ap|aa))?(?:\\s+\\d{5}(?:-\\d{4})?)?(?:\\s|$)',
];
const MILITARY_POSTAL_REGEX = new RegExp(MILITARY_POSTAL_PATTERNS.join('|'), 'i');
// Validation patterns for post-match checks
const INVALID_STATE_PATTERN = /(?:apo|fpo|dpo)\s+([a-z]{2})\s+\d{5}/i;
const NON_NUMERIC_ZIP_PATTERN = /(?:apo|fpo|dpo)\s+(?:ae|ap|aa)\s+([a-z]+)(?:\s|$)/i;
const WRONG_ZIP_LENGTH_PATTERN = /(?:apo|fpo|dpo)\s+(?:ae|ap|aa)\s+(\d{1,4}|\d{6,})(?:\s|$)/i;
const hasExcludedPattern = (addressLine) => {
    return EXCLUDE_PATTERNS.some((pattern) => pattern.test(addressLine));
};
const hasInvalidStateCode = (addressLine) => {
    const match = addressLine.match(INVALID_STATE_PATTERN);
    if (!match)
        return false;
    const stateCode = match[1].toLowerCase();
    return !MILITARY_STATE_CODES.includes(stateCode);
};
const hasInvalidZipFormat = (addressLine) => {
    return NON_NUMERIC_ZIP_PATTERN.test(addressLine) || WRONG_ZIP_LENGTH_PATTERN.test(addressLine);
};
export const isMilitaryPostal = (addressLine) => {
    // First check if this looks like a street address containing military codes
    if (hasExcludedPattern(addressLine)) {
        return false;
    }
    // Check if it matches military postal patterns
    const hasMatch = MILITARY_POSTAL_REGEX.test(addressLine);
    if (!hasMatch) {
        return false;
    }
    // Validate the match isn't a false positive
    if (hasInvalidStateCode(addressLine) || hasInvalidZipFormat(addressLine)) {
        return false;
    }
    return true;
};
/**
 * Optimized function to check if any address field contains military postal codes.
 * Combines all address fields into a single string and runs validation once,
 * with short-circuiting to avoid unnecessary function calls.
 */
export const hasAnyMilitaryPostal = (...addressFields) => {
    // Filter out empty/undefined fields and combine into single string
    const combinedAddress = addressFields.filter((field) => Boolean(field === null || field === void 0 ? void 0 : field.trim())).join(' ');
    // If no valid address fields, return false
    if (!combinedAddress) {
        return false;
    }
    // Run validation once on combined string
    return isMilitaryPostal(combinedAddress);
};
//# sourceMappingURL=militaryPostalValidation.js.map